import React, { useEffect } from 'react';
import styled from 'styled-components';
import { vibrateAchievement } from '../utils/haptics';
import { YatzyScores, Die, ScoreCategory } from '../types/game';
import { scoreCategories, calculatePossibleScore } from '../utils/gameLogic';
import { useToast } from '../context/ToastContext';
import { useI18n } from '../i18n';
import {
  ScoreCardContainer,
  SectionTitle,
  ScoreRow,
  CategoryInfo,
  StatusIcon,
  CategoryName,
  CategoryDescription,
  ScoreValue,
  SummaryRow,
  TotalScore,
} from './shared/ScoreCardStyles';

interface ScoreCardProps {
  scores: YatzyScores;
  dice: Die[];
  onScoreSelect: (category: keyof YatzyScores) => void;
  rollsLeft: number;
  upperSectionScore: number;
  upperSectionBonus: number;
  totalScore: number;
  highlightCategory?: string | null;
  forcedOrder?: (keyof YatzyScores)[]; // Om tvingande läge används: ordning på kategorier
  keyboardNav?: boolean; // enable keyboard navigation
  scoringType?: 'normal' | 'reverse' | 'multiplied';
  scoreMultiplier?: number; // For multiplied scoring modes
  /** Antal kast per omgång i aktuellt läge. Styr när kategorier blir valbara
   *  (minst ett kast måste vara gjort). Default 3 = klassiskt. */
  maxRolls?: number;
}

const BonusHint = styled.div`
  margin: 4px 0 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--bonus-hint-bg, rgba(255,255,255,0.12));
  color: var(--scorecard-text, #fff);
  font-size: 13px;
  backdrop-filter: blur(4px) saturate(140%);
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
`;

const ScoringIndicator = styled.div<{ $type: 'reverse' | 'multiplied' }>`
  background: ${props => props.$type === 'reverse' 
    ? 'linear-gradient(135deg, #ff5722, #d32f2f)' 
    : 'linear-gradient(135deg, #9c27b0, #673ab7)'};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
`;

// Flytta dessa till toppen av komponenten för återanvändning
const FACE_VALUE_MAP: Record<string, number> = { ones: 1, twos: 2, threes: 3, fours: 4, fives: 5, sixes: 6 };
const FACE_NAME_SINGULAR: Record<number, string> = { 1: 'etta', 2: 'tvåa', 3: 'trea', 4: 'fyra', 5: 'femma', 6: 'sexa' };
const FACE_NAME_PLURAL: Record<number, string> = { 1: 'ettor', 2: 'tvåor', 3: 'treor', 4: 'fyror', 5: 'femmor', 6: 'sexor' };

const PointingHandIcon = styled.span`
  position: absolute;
  left: -12px;
  font-size: 20px;
  opacity: 0.9;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 0.8; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.15); }
  }
`;

const ScoreCard = React.forwardRef<HTMLDivElement, ScoreCardProps>(({ 
  scores,
  dice,
  onScoreSelect,
  rollsLeft,
  upperSectionScore,
  upperSectionBonus,
  totalScore,
  highlightCategory,
  forcedOrder,
  keyboardNav = true,
  scoringType = 'normal',
  scoreMultiplier = 1,
  maxRolls = 3
}, ref) => {
  const { showConfirmationToast } = useToast();
  const { t, locale } = useI18n();
  // Haptik när bonus låses in första gången
  const bonusRef = React.useRef(false);
  useEffect(() => {
    if (!bonusRef.current && upperSectionBonus > 0) {
      bonusRef.current = true;
      try { vibrateAchievement(); } catch {}
    }
  }, [upperSectionBonus]);
  const upperSectionCategories = scoreCategories.filter(cat => cat.isUpperSection);
  const lowerSectionCategories = scoreCategories.filter(cat => !cat.isUpperSection);
  
  // CHANGE (2026-08-15): Härlett från maxRolls i stället för hårdkodat 3 – i
  // Maxi-multiplayer (fler kast) blev annars ingen kategori valbar.
  const canSelectScore = rollsLeft < maxRolls;
  const nextForcedKey = React.useMemo(()=> {
    if(!forcedOrder) return null;
    return forcedOrder.find(k => scores[k] === null) || null;
  }, [forcedOrder, scores]);
  
  const nextForcedCategoryName = React.useMemo(() => {
    if (!nextForcedKey) return null;
    return scoreCategories.find(c => c.key === nextForcedKey)?.name || '';
  }, [nextForcedKey]);

  // Bonus progress hint (classic: 63+)
  const threshold = 63;
  const needed = Math.max(0, threshold - upperSectionScore);
  const bonusAchieved = upperSectionBonus > 0;
  const remainingUpper = upperSectionCategories.filter(c => scores[c.key] === null);
  // Använd den globala konstanten istället för lokal deklaration
  const bestRemaining = remainingUpper
    .map(c => ({ key: c.key as string, value: FACE_VALUE_MAP[c.key as string] || 0 }))
    .sort((a, b) => b.value - a.value)[0];
  const maxRemainingUpper = remainingUpper.reduce((sum, c) => sum + (FACE_VALUE_MAP[c.key as string] || 0) * 5, 0);
  const bonusStillPossible = upperSectionScore + maxRemainingUpper >= threshold;

  const renderScoreSection = (categories: ScoreCategory[], title: string) => (
    <div>
      <SectionTitle>{title}</SectionTitle>
      {categories.map(category => {
        const currentScore = scores[category.key];
        const isEmpty = currentScore === null;
        const safeDice = dice || [] as any;
        // Calculate possible score - only if category is empty
        const possibleScore = isEmpty ? calculatePossibleScore(safeDice, category.key) : 0;
        // Only show as selectable if: can select, is empty, matches forced order (if any)
        // Note: We allow selection even if possibleScore is 0 to enable striking categories
        const isSelectable = canSelectScore && isEmpty && (!nextForcedKey || nextForcedKey === category.key);
        
        const isStruck = !isEmpty && currentScore === 0;
        const isCompleted = !isEmpty && (currentScore ?? 0) > 0;
        
        // CHANGE (2026-02-23): Handen ska visas i tvingande ordning (forced/reverse)
        // även om möjliga poäng är 0, eftersom spelaren *måste* kunna välja/stryka kategorin.
        // I icke-tvingande lägen visas handen bara när possibleScore > 0.
        const isForcedPick = !!nextForcedKey; // forcedOrder aktiv och vi är på "nästa" kategori
        const shouldShowHand = isSelectable && isEmpty && (possibleScore > 0 || isForcedPick);
    return (
          <ScoreRow
            key={category.key}
            $isSelectable={isSelectable}
            $isSelected={false}
            $isStruck={isStruck}
            $isCompleted={isCompleted}
            $shouldShowHand={shouldShowHand}
            $highlight={highlightCategory === category.key}
            onClick={() => {
              if (!isSelectable) return;
              // If possible score is 0 (would strike), ask for confirmation
              if (calculatePossibleScore(dice as any, category.key) === 0) {
                showConfirmationToast(t('game.strikeConfirm', { category: t(`cat.${String(category.key)}.name`) }), {
                  onConfirm: () => onScoreSelect(category.key),
                  confirmText: t('game.strikeYes'),
                  cancelText: t('game.strikeNo'),
                  replaceKey: 'strike-confirmation'
                });
                return;
              }
              onScoreSelect(category.key);
            }}
            tabIndex={keyboardNav && isSelectable ? 0 : -1}
            onKeyDown={(e) => {
              if (!keyboardNav) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (isSelectable) {
                  if (calculatePossibleScore(dice as any, category.key) === 0) {
                    showConfirmationToast(t('game.strikeConfirm', { category: t(`cat.${String(category.key)}.name`) }), {
                      onConfirm: () => onScoreSelect(category.key),
                      confirmText: t('game.strikeYes'),
                      cancelText: t('game.strikeNo'),
                      replaceKey: 'strike-confirmation'
                    });
                  } else {
                    onScoreSelect(category.key);
                  }
                }
              } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const focusable = Array.from((ref as any)?.current?.querySelectorAll('[data-score-row="1"]') || []) as HTMLElement[];
                const idx = focusable.indexOf(e.currentTarget as HTMLElement);
                if (idx >= 0) {
                  const nextIdx = e.key === 'ArrowDown' ? Math.min(focusable.length -1, idx +1) : Math.max(0, idx -1);
                  focusable[nextIdx]?.focus();
                }
              }
            }}
            data-score-row={isSelectable ? '1' : undefined}
          >
            {shouldShowHand && (
              <PointingHandIcon role="img" aria-label="Möjligt val">👉</PointingHandIcon>
            )}
            <CategoryInfo>
              <CategoryName>{t(`cat.${String(category.key)}.name`)}</CategoryName>
              <CategoryDescription>{t(`cat.${String(category.key)}.desc`)}</CategoryDescription>
            </CategoryInfo>
            <ScoreValue $isPossible={possibleScore > 0} $dim={isEmpty && (!isSelectable || possibleScore === 0)}>
              {/* CHANGE (2026-08-15): Visa (0) på valbara rader så stryk-kandidater syns direkt. */}
              {currentScore !== null ? currentScore : isSelectable ? `(${possibleScore})` : '–'}
            </ScoreValue>
            {(() => {
              if (currentScore === null) return null;
              if (currentScore === 0) return <StatusIcon $variant="cross" aria-label="Nollad">✕</StatusIcon>;
              // Använd global konstant
              if (FACE_VALUE_MAP[category.key as string]) {
                const fv = FACE_VALUE_MAP[category.key as string];
                const count = currentScore / fv;
                if (count >= 4) return <StatusIcon $variant="star" aria-label="Hög träff">★</StatusIcon>;
                if (count === 3) return <StatusIcon $variant="check" aria-label="Tre av ett tal">✓</StatusIcon>;
                return <StatusIcon $variant="cross" aria-label="Mindre än tre">✕</StatusIcon>;
              }
              // Lower section: star if >=80% of max value
              const maxMap: Record<string, number> = {
                onePair:12, twoPairs:22, threeOfAKind:18, fourOfAKind:24, smallStraight:15, largeStraight:20, fullHouse:28, chance:30, yatzy:50
              };
              const max = maxMap[category.key as string];
              if (max) {
                if (currentScore >= 0.8 * max) return <StatusIcon $variant="star" aria-label="Topppoäng">★</StatusIcon>;
                return <StatusIcon $variant="check" aria-label="Klar">✓</StatusIcon>;
              }
              return null;
            })()}
          </ScoreRow>
        );
      })}
    </div>
  );

  return (
  <ScoreCardContainer ref={ref}>
      {renderScoreSection(upperSectionCategories, t('score.upper'))}
      
  <SummaryRow>
        <span>{t('score.upperSum')}</span>
        <span style={{display:'flex',alignItems:'center',gap:10}}>
          {upperSectionScore}
          {(() => {
            // New simplified indicator logic per spec:
            // ⭐ = bonus uppnådd (>= tröskel)
            // 🙂 = fortfarande chans att nå bonus (inte uppnådd men matematiskt möjlig)
            // 😢 = omöjligt att nå bonus med återstående kategorier
            if (upperSectionScore >= threshold) {
              return <span role="img" aria-label="Bonus uppnådd" style={{fontSize:26,filter:'drop-shadow(0 2px 3px rgba(0,0,0,.55))'}}>⭐</span>;
            }
            if (!bonusStillPossible) {
              return <span role="img" aria-label="Bonus omöjlig" style={{fontSize:24,filter:'drop-shadow(0 2px 3px rgba(0,0,0,.45))'}}>😢</span>;
            }
            return <span role="img" aria-label="Bonus fortfarande möjlig" style={{fontSize:24,filter:'drop-shadow(0 2px 3px rgba(0,0,0,.45))'}}>🙂</span>;
          })()}
        </span>
      </SummaryRow>
      {forcedOrder && nextForcedKey && nextForcedCategoryName && (
        <BonusHint style={{marginTop:4, background:'rgba(255,255,255,0.08)'}}>
          {t('score.forcedNext')} <strong>{nextForcedCategoryName}</strong>
        </BonusHint>
      )}

      {!bonusAchieved && needed > 0 && bonusStillPossible && (
        <BonusHint>
          {(() => {
            const close = needed <= 12;
            if (!bestRemaining || locale !== 'sv') return t('score.bonusLeft', { points: needed });
            const face = bestRemaining.value;
            const perMark = face;
            const countNeeded = Math.max(1, Math.ceil(needed / perMark));
            if (close && face >= 4) {
              const word = countNeeded === 1 ? FACE_NAME_SINGULAR[face] : FACE_NAME_PLURAL[face];
              return `Du behöver ${countNeeded} ${word} för bonus`;
            }
            return t('score.bonusLeft', { points: needed });
          })()}
        </BonusHint>
      )}
      
      <SummaryRow>
        <span>{t('score.bonusRow')}</span>
        <span>{upperSectionBonus}</span>
      </SummaryRow>
      
      {renderScoreSection(lowerSectionCategories, t('score.lower'))}
      
      <TotalScore>
        {t('score.total', { total: totalScore })}
        {scoringType === 'reverse' && (
          <ScoringIndicator $type="reverse">
            <span role="img" aria-label="Reverse">🔄</span> {t('score.reverseNote')}
          </ScoringIndicator>
        )}
        {scoringType === 'multiplied' && (
          <ScoringIndicator $type="multiplied">
            <span role="img" aria-label="Multiplied">✖️</span> 
            {scoreMultiplier > 1 ? `${scoreMultiplier}x multiplikator!` : 'Multiplicerad poäng!'}
          </ScoringIndicator>
        )}
      </TotalScore>
    </ScoreCardContainer>
  );
});

export default ScoreCard;
